import React, { useEffect, useState } from 'react';

interface FrameAnimationProps {
  frames: string[];           // Array of image paths
  animationName: string;      // Unique animation name
  duration?: number;          // Animation duration in seconds
  isPlaying?: boolean;        // Whether the animation is playing
  loop?: boolean;             // Whether to loop the animation
  iterationCount?: number | 'infinite'; // Custom iteration count (overrides loop)
  className?: string;         // Additional CSS class
  style?: React.CSSProperties; // Inline styles
  onAnimationEnd?: () => void; // Callback when animation ends
}

const FrameAnimation: React.FC<FrameAnimationProps> = ({
  frames,
  animationName,
  duration = 2,
  isPlaying = true,
  loop = true,
  iterationCount,
  className = '',
  style = {},
  onAnimationEnd
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check if the style tag for this animation already exists
    if (!document.getElementById(animationName)) {
        // Generate keyframes CSS
        const keyframeRules = frames.map((img, index) => {
          const percentage = (index / frames.length) * 100;
          return `${percentage}% { background-image: url(${img}); }`;
        });

        // Add 100% keyframe to ensure smooth looping back to the first frame
        // Add 100% keyframe to ensure smooth looping back to the first frame
        //keyframeRules.push(`100% { background-image: url(${frames[0]}); }`);
        // Add 100% keyframe
        // If looping, go back to first frame for smooth loop
        // If not looping, stay on last frame
        keyframeRules.push(`100% { background-image: url(${loop ? frames[0] : frames[frames.length - 1]}); }`);

        const keyframes = keyframeRules.join(" ");

        // Create and append style tag
        const styleTag = document.createElement("style");
        styleTag.id = animationName;
        styleTag.innerHTML = `
          @keyframes ${animationName} {
            ${keyframes}
          }
        `;
        document.head.appendChild(styleTag);
    }

    // Preload images manually to ensure smooth playback
    let isMounted = true;

    // Helper to check if image is already cached/loaded
    const checkImage = (path: string) => {
        return new Promise<void>((resolve) => {
            const img = new Image();
            img.src = path;
            if (img.complete) {
                resolve();
            } else {
                img.onload = () => resolve();
                img.onerror = () => resolve(); // Proceed even on error to avoid blocking
            }
        });
    };

    Promise.all(frames.map(checkImage)).then(() => {
        if (isMounted) setIsLoaded(true);
    });
    
    return () => {
        isMounted = false;
    };
  }, [frames, animationName, loop]);

  return (
    <>
      {/* Keep preload links as they help browser prioritize */}
      {frames.map((frame, index) => (
        <link key={index} rel="preload" href={frame} as="image" />
      ))}
      <div
        className={className}
        style={{
          ...style,
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          ...(isPlaying && isLoaded
            ? {
                animation: `${animationName} ${duration}s steps(${frames.length}) ${
                  iterationCount !== undefined ? iterationCount : (loop ? 'infinite' : 1)
                } ${loop ? '' : 'forwards'}`
              }
            : { backgroundImage: `url('${frames[0]}')` })
        }}
        onAnimationEnd={onAnimationEnd}
      />
    </>
  );
};

export default FrameAnimation;
